import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Code } from 'lucide-react';
import { MetricCard } from './components/MetricCard';
import { ActivitySection } from './components/ActivitySection';
import { QuickActions } from './components/QuickActions';
import { EmptyStateCard } from './components/EmptyStateCard';
import { OnboardingView } from './components/OnboardingView';
import { InboxSection } from './components/InboxSection';
import { CompactATSConnectionBadge } from './components/ATSConnectionBadge';
import { DevStateToggle } from './components/DevStateToggle';
import { isOverrideActive } from './utils/devStateOverride';
import {
  DashboardData,
  DashboardMetrics,
  UserState,
  UserCapabilities,
  UserContext,
  InboxItem,
} from './utils/dashboardTypes';

interface DashboardLayoutProps {
  data: DashboardData;
  metrics: DashboardMetrics;
  userState: UserState;
  capabilities: UserCapabilities;
  userContext: UserContext;
  inboxItems: InboxItem[];
  onAddRole: () => void;
  onAddCandidate: () => void;
  onInviteUser: () => void;
  onConnectBullhorn: () => void;
  onConnectJobAdder: () => void;
  onCompleteProfile?: () => void;
  onSetPreferences?: () => void;
  onExploreMarketplace?: () => void;
  onTakeTour?: () => void;
  onViewHelp?: () => void;
  onDevStateChange?: () => void;
  showDevTools?: boolean;
}

export function DashboardLayout({
  data,
  metrics,
  userState,
  capabilities,
  userContext,
  inboxItems,
  onAddRole,
  onAddCandidate,
  onInviteUser,
  onConnectBullhorn,
  onConnectJobAdder,
  onCompleteProfile,
  onSetPreferences,
  onExploreMarketplace,
  onTakeTour,
  onViewHelp,
  onDevStateChange,
  showDevTools = false,
}: DashboardLayoutProps) {
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const renderDevTools = () => {
    if (!showDevTools) return null;
    return (
      <DevStateToggle
        currentState={userState}
        onStateChange={onDevStateChange || (() => {})}
      />
    );
  };

  const renderDevIndicator = () => {
    if (!showDevTools || !isOverrideActive()) return null;
    return (
      <div className={cn(
        "col-span-full",
        "flex items-center justify-center gap-2",
        "px-3 py-1.5",
        "bg-gradient-to-r from-amber-100 to-amber-200",
        "dark:from-amber-900/40 dark:to-amber-800/40",
        "border-b border-amber-400 dark:border-amber-600",
        "text-xs font-semibold text-amber-900 dark:text-amber-200"
      )}>
        <Code className="w-3.5 h-3.5" />
        Dev Mode Active: Dashboard state is overridden
      </div>
    );
  };

  // NEW USER STATE: Onboarding view
  if (userState === 'new_user') {
    return (
      <>
        {/* Dashboard Header */}
        <header className={cn(
          "col-span-full row-start-1",
          "flex items-center justify-between",
          "px-6 h-[72px]",
          "border-b border-slate-200/80 dark:border-slate-800/80",
          "bg-gradient-to-r from-white via-white to-slate-50/50",
          "dark:from-slate-950 dark:via-slate-950 dark:to-slate-900/50",
          "animate-fade-in"
        )}>
          <div className="flex items-baseline gap-3">
            <h1 className="font-serif text-3xl text-slate-900 dark:text-slate-100 tracking-tight">
              Dashboard
            </h1>
            <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">
              Getting Started
            </span>
          </div>
          <div className="flex items-center gap-4">
            {renderDevTools()}
          </div>
        </header>

        {renderDevIndicator()}

        {/* Main scrollable content area - full width */}
        <div className={cn(
          "col-span-full row-start-2",
          "overflow-y-auto overflow-x-hidden",
          "px-6 lg:px-8 py-6",
          // Subtle gradient background
          "bg-gradient-to-br from-slate-50 via-white to-slate-50/80",
          "dark:from-slate-950 dark:via-slate-900 dark:to-slate-950",
          // Custom scrollbar
          "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-slate-300/50",
          "hover:scrollbar-thumb-slate-400/60",
          "dark:scrollbar-thumb-slate-700/50 dark:hover:scrollbar-thumb-slate-600/60"
        )}>
          <OnboardingView
            userContext={userContext}
            metrics={metrics}
            capabilities={capabilities}
            userProfile={data.userProfile}
            onAddRole={onAddRole}
            onAddCandidate={onAddCandidate}
            onConnectBullhorn={onConnectBullhorn}
            onConnectJobAdder={onConnectJobAdder}
            onCompleteProfile={onCompleteProfile}
            onSetPreferences={onSetPreferences}
            onExploreMarketplace={onExploreMarketplace}
            onTakeTour={onTakeTour}
            onViewHelp={onViewHelp}
          />
        </div>

        {/* Empty actions bar for layout consistency */}
        <div className={cn(
          "col-span-full row-start-3",
          "h-[64px]",
          "border-t border-slate-200/50 dark:border-slate-800/50",
          "bg-slate-50/50 dark:bg-slate-900/50"
        )} />
      </>
    );
  }

  // EMPTY STATE: Has ATS but no data
  if (userState === 'empty_state') {
    const hasAts = data.connections && data.connections.length > 0;

    return (
      <>
        {/* Dashboard Header */}
        <header className={cn(
          "col-span-full row-start-1",
          "flex items-center justify-between",
          "px-6 h-[72px]",
          "border-b border-slate-200/80 dark:border-slate-800/80",
          "bg-gradient-to-r from-white via-white to-slate-50/50",
          "dark:from-slate-950 dark:via-slate-950 dark:to-slate-900/50",
          "animate-fade-in"
        )}>
          <div className="flex items-baseline gap-3">
            <h1 className="font-serif text-3xl text-slate-900 dark:text-slate-100 tracking-tight">
              Dashboard
            </h1>
            <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">
              Overview
            </span>
          </div>
          <div className="flex items-center gap-4">
            {renderDevTools()}
            {hasAts && userContext.atsProviderNames.length > 0 && (
              <CompactATSConnectionBadge providerNames={userContext.atsProviderNames} />
            )}
          </div>
        </header>

        {renderDevIndicator()}

        {/* Main scrollable content area */}
        <div className={cn(
          "col-span-full row-start-2",
          "overflow-y-auto overflow-x-hidden",
          "px-6 py-6",
          "bg-white dark:bg-slate-950"
        )}>
          <EmptyStateCard
            type="roles"
            hasAtsConnection={hasAts}
            canImport={capabilities.canConnectAts}
            onAddManually={onAddRole}
            onImportFromAts={() => handleNavigate('/settings/connections')}
          />
          <EmptyStateCard
            type="candidates"
            hasAtsConnection={hasAts}
            canImport={capabilities.canConnectAts}
            onAddManually={onAddCandidate}
            onImportFromAts={() => handleNavigate('/settings/connections')}
          />
        </div>

        {/* Quick actions at bottom */}
        <div className={cn(
          "col-span-full row-start-3",
          "flex items-center gap-4",
          "px-4 py-2 h-[64px]",
          "border-t border-slate-200/50 dark:border-slate-800/50",
          "bg-slate-50/70 dark:bg-slate-900/70",
          "backdrop-blur-sm"
        )}>
          <QuickActions
            capabilities={capabilities}
            onAddRole={onAddRole}
            onAddCandidate={onAddCandidate}
            onInviteUser={onInviteUser}
            onNavigate={handleNavigate}
            horizontal={true}
          />
        </div>
      </>
    );
  }

  // GROWTH MODE: Simplified metrics with clean, non-clipping layout
  if (userState === 'growth_mode') {
    return (
      <>
        {/* Dashboard Header */}
        <header className={cn(
          "col-span-full row-start-1",
          "flex items-center justify-between",
          "px-6 h-[72px]",
          "border-b border-slate-200/80 dark:border-slate-800/80",
          "bg-gradient-to-r from-white via-white to-slate-50/50",
          "dark:from-slate-950 dark:via-slate-950 dark:to-slate-900/50",
          "animate-fade-in"
        )}>
          <div className="flex items-baseline gap-3">
            <h1 className="font-serif text-3xl text-slate-900 dark:text-slate-100 tracking-tight">
              Dashboard
            </h1>
            <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">
              Overview
            </span>
          </div>
          <div className="flex items-center gap-4">
            {renderDevTools()}
            {userContext.atsProviderNames.length > 0 && (
              <CompactATSConnectionBadge providerNames={userContext.atsProviderNames} />
            )}
          </div>
        </header>

        {renderDevIndicator()}

        {/* Main scrollable content area */}
        <div className={cn(
          "col-span-full row-start-2",
          "overflow-y-auto overflow-x-hidden",
          "px-6 py-6",
          // Subtle gradient background
          "bg-gradient-to-br from-slate-50 via-white to-slate-50/80",
          "dark:from-slate-950 dark:via-slate-900 dark:to-slate-950",
          // Custom scrollbar
          "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-slate-300/50",
          "hover:scrollbar-thumb-slate-400/60",
          "dark:scrollbar-thumb-slate-700/50 dark:hover:scrollbar-thumb-slate-600/60"
        )}>
          {/* Metrics Row - Horizontal strip */}
          <div className={cn(
            "grid gap-4 mb-6",
            "grid-cols-2",
            "lg:grid-cols-4"
          )}>
            <MetricCard
              label="Roles"
              value={metrics.totalRoles}
              icon="Briefcase"
              onClick={() => handleNavigate('/my-roles')}
            />
            <MetricCard
              label="Candidates"
              value={metrics.totalCandidates}
              icon="Users"
              onClick={() => handleNavigate('/my-candidates')}
            />
            <MetricCard
              label="Matches"
              value={metrics.totalMatches}
              icon="UserCheck"
              onClick={() => handleNavigate('/matches')}
            />
            <MetricCard
              label="Messages"
              value={metrics.unreadMessages}
              icon="MessageSquare"
              onClick={() => handleNavigate('/messages')}
            />
          </div>

          {/* Content Panels - Side by side on large screens */}
          <div className={cn(
            "grid gap-6",
            "grid-cols-1",
            "lg:grid-cols-2"
          )}>
            {/* Inbox Panel */}
            <section className={cn(
              "bg-white dark:bg-slate-900/80",
              "border border-slate-200/60 dark:border-slate-700/60",
              "rounded-xl",
              "shadow-sm",
              "overflow-hidden"
            )}>
              <div className="p-5">
                <InboxSection items={inboxItems} limit={5} />
              </div>
            </section>

            {/* Activity Panel */}
            <section className={cn(
              "bg-white dark:bg-slate-900/80",
              "border border-slate-200/60 dark:border-slate-700/60",
              "rounded-xl",
              "shadow-sm",
              "overflow-hidden"
            )}>
              <div className="p-5">
                <ActivitySection
                  metrics={metrics}
                  onNavigate={handleNavigate}
                  limit={3}
                  compact={true}
                />
              </div>
            </section>
          </div>
        </div>

        {/* Quick actions at bottom */}
        <div className={cn(
          "col-span-full row-start-3",
          "flex items-center gap-4",
          "px-4 py-2 h-[64px]",
          "border-t border-slate-200/50 dark:border-slate-800/50",
          "bg-slate-50/80 dark:bg-slate-900/80",
          "backdrop-blur-sm"
        )}>
          <QuickActions
            capabilities={capabilities}
            onAddRole={onAddRole}
            onAddCandidate={onAddCandidate}
            onInviteUser={onInviteUser}
            onNavigate={handleNavigate}
            horizontal={true}
          />
        </div>
      </>
    );
  }

  // ESTABLISHED USER: Full dashboard with horizontal layout
  return (
    <>
      {(userContext.atsProviderNames.length > 0 || showDevTools) && (
        <header className={cn(
          "col-span-full row-start-1",
          "flex items-center justify-between",
          "px-6 h-[72px]",
          "border-b border-slate-200/80 dark:border-slate-800/80",
          "bg-gradient-to-r from-white via-white to-slate-50/50",
          "dark:from-slate-950 dark:via-slate-950 dark:to-slate-900/50",
          "animate-fade-in"
        )}>
          <div className="flex items-baseline gap-3">
            <h1 className="font-serif text-3xl text-slate-900 dark:text-slate-100 tracking-tight">
              Dashboard
            </h1>
            <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">
              Overview
            </span>
          </div>
          <div className="flex items-center gap-4">
            {renderDevTools()}
            {userContext.atsProviderNames.length > 0 && (
              <CompactATSConnectionBadge providerNames={userContext.atsProviderNames} />
            )}
          </div>
        </header>
      )}

      {renderDevIndicator()}

      {/* Metric Sidebar (vertical stack) */}
      <aside className={cn(
        "col-start-1 row-start-2",
        "flex flex-col gap-0",
        "border-r-2 border-slate-200 dark:border-slate-800",
        "bg-gradient-to-b from-slate-50 to-white",
        "dark:from-slate-950 dark:to-slate-900",
        "overflow-y-auto overflow-x-hidden",
        // Custom scrollbar
        "scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-slate-300",
        "dark:scrollbar-track-slate-950 dark:scrollbar-thumb-slate-700"
      )}>
        <MetricCard
          label="Open Roles"
          value={metrics.openRoles}
          icon="Briefcase"
          subtitle={`${metrics.privateRoles} private, ${metrics.publicSharedRoles} shared`}
          onClick={() => handleNavigate('/my-roles')}
          compact={true}
        />
        <MetricCard
          label="Total Candidates"
          value={metrics.totalCandidates}
          icon="Users"
          onClick={() => handleNavigate('/my-candidates')}
          compact={true}
        />
        <MetricCard
          label="Active Matches"
          value={metrics.activeMatches}
          icon="UserCheck"
          subtitle={`${metrics.newMatches} new`}
          onClick={() => handleNavigate('/matches')}
          compact={true}
        />
        <MetricCard
          label="Unread Messages"
          value={metrics.unreadMessages}
          icon="MessageSquare"
          onClick={() => handleNavigate('/messages')}
          compact={true}
        />
      </aside>

      {/* Unified Activity Feed */}
      <main className={cn(
        "col-start-2 row-start-2",
        "overflow-y-auto overflow-x-hidden",
        "px-6 py-6 lg:px-10 lg:py-8 xl:px-12 xl:py-10",
        "bg-white dark:bg-slate-950",
        // Custom scrollbar
        "scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-slate-300",
        "hover:scrollbar-thumb-slate-400",
        "dark:scrollbar-track-slate-950 dark:scrollbar-thumb-slate-700",
        "dark:hover:scrollbar-thumb-slate-600"
      )}>
        <InboxSection items={inboxItems} limit={3} />
        <ActivitySection
          metrics={metrics}
          onNavigate={handleNavigate}
          horizontal={true}
          limit={3}
        />
      </main>

      {/* Quick Actions Bar */}
      <div className={cn(
        "col-span-full row-start-3",
        "flex items-center gap-4",
        "px-4 py-2 h-[64px]",
        "border-t border-slate-200/50 dark:border-slate-800/50",
        "bg-slate-50/70 dark:bg-slate-900/70",
        "backdrop-blur-sm"
      )}>
        <QuickActions
          capabilities={capabilities}
          onAddRole={onAddRole}
          onAddCandidate={onAddCandidate}
          onInviteUser={onInviteUser}
          onNavigate={handleNavigate}
          horizontal={true}
        />
      </div>
    </>
  );
}
